import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FiArrowLeft, FiPlay, FiLock, FiCheck, FiClock, FiUsers, FiZap, FiBook
} from 'react-icons/fi';
import './CourseDetail.css';
import { academyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import { getAcademyCourseBySlug } from './catalog';

const CourseDetail = () => {
  const { courseId } = useParams();
  const { isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    loadCourseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadCourseData = async () => {
    try {
      setLoading(true);
      const { data } = await academyAPI.getCourse(courseId);
      setCourse(data.course);
      setLessons(Array.isArray(data.lessons) ? data.lessons : []);
      setIsEnrolled(!!data.enrolled);
    } catch (error) {
      console.error('Error loading course:', error);

      // Fallback: local catalog allows browsing without backend
      const local = getAcademyCourseBySlug(courseId);
      if (local) {
        const totalLessons = (local.units || []).reduce((acc, u) => acc + (u.lessons?.length || 0), 0);
        setCourse({
          id: local.slug,
          slug: local.slug,
          title: local.title,
          description: local.description,
          fullDescription: local.description,
          category: local.category,
          difficulty: local.difficulty,
          duration: local.duration,
          totalLessons,
          completedLessons: 0,
          xpReward: Number(local.xpReward || 0),
          enrolled: 0,
          rating: null,
          reviews: 0,
          image: local.image,
          instructor: {
            name: local.instructor || 'Graphyra',
            avatar: null,
            title: null,
            courses: 0,
            students: 0
          },
          skills: [],
          requirements: [],
          includes: [],
          hasGame: !!local.hasGame,
          gameTitle: null,
          certificate: false
        });
        setLessons((local.units || []).map((u) => ({
          id: u.id,
          title: u.title,
          lessons: (u.lessons || []).map((l) => ({
            id: l.slug,
            slug: l.slug,
            title: l.title,
            duration: Number(l.duration || 0),
            type: 'guided',
            completed: false,
            xp: Number(l.xp || 0),
            locked: !isAuthenticated
          }))
        })));
        setIsEnrolled(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      default: return '📖';
    }
  };

  const getProgressPercentage = () => {
    if (!course) return 0;
    return Math.round((course.completedLessons / course.totalLessons) * 100);
  };

  const getTotalXP = () => {
    let total = 0;
    lessons.forEach(unit => {
      unit.lessons.forEach(lesson => {
        if (lesson.completed) total += lesson.xp;
      });
    });
    return total;
  };

  const getNextLesson = () => {
    for (const unit of lessons) {
      for (const lesson of unit.lessons) {
        if (!lesson.completed && !lesson.locked) {
          return lesson;
        }
      }
    }
    return null;
  };

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      await academyAPI.enroll(courseId);
      setIsEnrolled(true);
      await loadCourseData();
    } catch (e) {
      console.error('Error enrolling:', e);
    }
  };

  if (loading) {
    return (
      <div className="course-loading">
        <div className="loading-spinner">📚</div>
        <p>Cargando curso...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="course-not-found">
        <h2>Curso no encontrado</h2>
        <Link to="/academy" className="back-link">
          <FiArrowLeft /> Volver a Academia
        </Link>
      </div>
    );
  }

  const nextLesson = getNextLesson();
  const courseIdentifier = course.slug || course.id;

  return (
    <div className="course-detail-page">
      {!isAuthenticated && showLoginPrompt && (
        <LoginPrompt
          message="Inicia sesión para inscribirte y guardar tu progreso"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
      {/* Hero */}
      <section className="course-hero">
        <div className="hero-background">
          <img src={course.image} alt={course.title} />
          <div className="hero-gradient"></div>
        </div>

        <div className="hero-content">
          <Link to="/academy" className="back-btn">
            <FiArrowLeft /> Volver
          </Link>
          <div className="course-badges">
            <span className="category-badge">{course.category}</span>
            <span className="difficulty-badge">{course.difficulty}</span>
            {course.hasGame && <span className="game-badge">🎮 Con juegos</span>}
            {course.certificate && <span className="cert-badge">📜 Certificado</span>}
          </div>

          <h1>{course.title}</h1>
          <p className="course-description">{course.description}</p>

          <div className="course-meta">
            <span><FiClock /> {course.duration}</span>
            <span><FiBook /> {course.totalLessons} lecciones</span>
            <span><FiUsers /> {Number(course.enrolled || 0).toLocaleString()} estudiantes</span>
          </div>

          {isEnrolled ? (
            <div className="enrolled-actions">
              <div className="progress-info">
                <div className="progress-bar-large">
                  <div className="progress-fill" style={{ width: `${getProgressPercentage()}%` }}></div>
                </div>
                <span>{getProgressPercentage()}% completado • {getTotalXP()} XP ganados</span>
              </div>
              {nextLesson && (
                <Link to={`/academy/lesson/${courseIdentifier}/${nextLesson.slug || nextLesson.id}`} className="btn-continue">
                  <FiPlay /> Continuar: {nextLesson.title}
                </Link>
              )}
            </div>
          ) : (
            <div className="enroll-actions">
              <button className="btn-enroll" onClick={handleEnroll}>
                <FiPlay /> Comenzar curso gratis
              </button>
              <span className="xp-info"><FiZap /> Gana hasta {course.xpReward} XP</span>
            </div>
          )}
        </div>
      </section>

      <div className="course-content">
        {/* Sidebar izquierdo - Contenido del curso */}
        <div className="course-sidebar">
          <div className="sidebar-section">
            <h3>📚 Contenido del curso</h3>
            <div className="units-list">
              {lessons.map((unit, index) => {
                const unitCompleted = unit.lessons.every(l => l.completed);
                const unitProgress = unit.lessons.filter(l => l.completed).length;
                
                return (
                  <div 
                    key={unit.id} 
                    className={`unit-item ${selectedUnit === index ? 'expanded' : ''}`}
                  >
                    <button 
                      className="unit-header"
                      onClick={() => setSelectedUnit(selectedUnit === index ? -1 : index)}
                    >
                      <div className="unit-status">
                        {unitCompleted ? (
                          <span className="status-complete"><FiCheck /></span>
                        ) : (
                          <span className="status-progress">{unitProgress}/{unit.lessons.length}</span>
                        )}
                      </div>
                      <div className="unit-info">
                        <h4>Unidad {index + 1}</h4>
                        <span>{unit.title}</span>
                      </div>
                    </button>
                    
                    {selectedUnit === index && (
                      <div className="lessons-list">
                        {unit.lessons.map(lesson => (
                          <Link
                            key={lesson.slug || lesson.id}
                            to={lesson.locked ? '#' : `/academy/lesson/${courseIdentifier}/${lesson.slug || lesson.id}`}
                            className={`lesson-item ${lesson.completed ? 'completed' : ''} ${lesson.locked ? 'locked' : ''}`}
                            onClick={(e) => {
                              if (!lesson.locked) return;
                              e.preventDefault();
                              setShowLoginPrompt(true);
                            }}
                          >
                            <span className="lesson-icon">{getLessonIcon(lesson.type)}</span>
                            <div className="lesson-info">
                              <span className="lesson-title">{lesson.title}</span>
                              <span className="lesson-meta">{lesson.duration} min • +{lesson.xp} XP</span>
                            </div>
                            <div className="lesson-status">
                              {lesson.completed && <FiCheck className="check-icon" />}
                              {lesson.locked && <FiLock className="lock-icon" />}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="course-main">
          {/* About */}
          <section className="about-section">
            <h2>Acerca de este curso</h2>
            <p>{course.fullDescription}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
